/**
 * `SuggestionsModal` — per-field editable "Review proposed change"
 * surface (ADR-0125, restoring the prototype shape from
 * `docs/reference/Foyer_Dashboard_CURRENT.jsx:5876-6010`).
 *
 * This component replaces the prior batch-checklist modal (ADR-0114
 * Slice B3) — the founder's repeated complaint was "a window that
 * cannot be edited," because the LLM occasionally produces a value
 * that's CLOSE but not quite right and the previous flow forced the
 * merchant to apply-as-is or cancel. The new modal:
 *
 *   - Reviews ONE field at a time (the parent owns the "which field is
 *     open" state — Visibility.tsx renders a per-row "Review" button).
 *   - Renders the proposed value in an editable input pre-filled with
 *     the LLM's suggestion.
 *   - Re-renders a JSON-LD diff live as the merchant types.
 *   - Shows a "you edited the proposed value" amber banner when the
 *     input differs from the original suggestion.
 *   - Gates the Apply button behind a required "I've reviewed the
 *     source and the proposed value" checkbox.
 *
 * SECURITY POSTURE (read before changing):
 *
 *  - MH-2 / MH-5 (ADR-0125): the source URL passed in via
 *    `source_page_url` is rendered as INERT TEXT — never a link, never
 *    interpolated into `href` / `src` / `style` / `onClick`. The URL is
 *    attacker-influenceable (it's a server-known URL derived from the
 *    merchant's site, which is itself the untrusted surface this
 *    extractor reads). A live link would re-open the prompt-injection
 *    vector that ADR-0040 §3.2 closes; inert text removes the gadget.
 *  - MH-3 (no-auto-apply, ADR-0040 §4.4 + ADR-0114 §4.4): the
 *    editable input's `onChange` writes to LOCAL React state only. The
 *    write authority is the merchant's `Approve & apply` click, which
 *    fires `onApply` and routes through the parent's
 *    `useAgentCardProfileApply` hook → `POST /v1/sites/:site_id/
 *    agent-card-profile`. The server validator (`validate.ts`) re-
 *    sanitizes the merchant-typed value. There is no client-side
 *    validation bypass — the modal never writes the card directly.
 *  - MH-4 (forensic hash discipline, ADR-0114 §5.1): the
 *    `suggestion_content_hash` carried in the apply's `source_hint` is
 *    computed by the PARENT over the DISPLAYED `suggestion.value`
 *    (NOT the merchant's edited value). The diff between
 *    `suggestion_content_hash` (what we showed) and the server-side
 *    `applied_profile_content_hash` (what hit the row) is the forensic
 *    "did the merchant edit?" signal. {@link
 *    computeSuggestionContentHash} runs in the parent's `handleApply`
 *    handler — this component never recomputes the hash.
 *  - Render discipline (ADR-0040 §3.2 defense 4 + ADR-0114 §7): the
 *    `value` and `rationale` strings come from
 *    `ProfileExtractionResult.suggested_card_profile_fields` for
 *    `source: 'scrape'` rows — UNTRUSTED LLM output over scraped
 *    merchant-site content. They are rendered as JSX text nodes
 *    (React auto-escapes), NEVER via `dangerouslySetInnerHTML`,
 *    NEVER interpolated into `href` / `src` / `style` / `onClick`. A
 *    `<script>` in a value/rationale is therefore inert escaped text.
 *  - Structured-sourced rows (`source: 'structured'`, ADR-0118 Slice B)
 *    are still EDITABLE in this modal and still gated by the same
 *    checkbox. Catalog/compliance text is merchant-uploaded (not
 *    scrape-controlled), but the same inert-render discipline applies.
 *    The rationale row is omitted for structured rows and a
 *    "FROM YOUR CATALOG / COMPLIANCE" inline badge replaces the
 *    analyzer attribution.
 *
 * RAW QUOTE / LINE NUMBER DELIBERATELY ABSENT:
 *
 *  The prototype rendered a verbatim "RAW QUOTE · line N" panel. We do
 *  NOT restore that here (ADR-0125 §Decision · Modal contract). LLM-
 *  emitted citation strings are hallucinable provenance — a wrong
 *  "line 47" is actively misleading for a trust product — and the
 *  citation text is itself attacker-influenceable, re-opening the
 *  fence-marker vector that `packages/profile-extraction/src/prompt-
 *  template.ts:141` closed. The trustworthy substitute is the server-
 *  known source page URL (rendered inert) plus the LLM rationale.
 *
 * Multi-language posture (ADR-0114 §1):
 *
 *  - `value` + `rationale` may be in any language.
 *  - The editable input renders with `dir="auto"` (browser auto-
 *    selects LTR / RTL per leading strong character). Text containers
 *    likewise carry `dir="auto"` + `overflow-wrap: anywhere` so RTL /
 *    CJK strings wrap rather than overflow.
 *
 * Apply-result handling (ADR-0114 §7 + §5.1):
 *
 *  - 200 → parent closes the modal + fires `onApplied` toast.
 *  - 412 (`concurrency_mismatch`) → modal STAYS OPEN with a banner +
 *    Refresh button calling `onRefreshRequested`.
 *  - 403 → parent closes + coral toast.
 *  - 400 → inline footer error with server `reason` + offending field.
 *  - 404 → parent closes + coral toast.
 */

import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import {
  AlertCircle,
  AlertTriangle,
  Check,
  Eye,
  Info,
  Sparkles,
  X,
} from 'lucide-react';

import {
  PROFILE_FIELDS,
  type ProfileSuggestedCardProfileField,
} from '@sill/types';

import {
  APPLY_FIELD_NAMES,
  type ApplySourceHint,
} from '../../api/agent-card-profile.js';
import {
  routeFor,
  validateFieldValue,
  type AgentCardEditableFieldId,
} from '../shared/agent-card-field-routing.js';
import { useC } from '../../lib/theme.js';
import { Modal } from '../../components/atoms/Modal.js';

const monoFont = "'JetBrains Mono', monospace";
const sansFont = "'Geist', sans-serif";

/** A suggestion is "applyable" only when its `field_id` is one of the
 *  ten editable fields ADR-0127 ships: seven `agent_card_profile`
 *  cards plus three `site_compliance` fields. `name` is intentionally
 *  NOT in this set — `name` is set during onboarding, never via the
 *  per-field modal. */
function isApplyableFieldId(id: string): id is AgentCardEditableFieldId {
  if ((APPLY_FIELD_NAMES as readonly string[]).includes(id)) return true;
  return routeFor(id) !== undefined;
}

/** Human-facing labels for the three compliance fields (ADR-0127). The
 *  seven card fields live in `PROFILE_FIELDS`; compliance fields don't
 *  appear there because they're disjoint from the profile field union
 *  (single-storage-home invariant from ADR-0118 / ADR-0127). */
const COMPLIANCE_LABELS: Record<
  'returns_policy' | 'seller_tos_url' | 'seller_privacy_policy_url',
  string
> = {
  returns_policy: 'Returns policy',
  seller_tos_url: 'Seller terms-of-service URL',
  seller_privacy_policy_url: 'Seller privacy-policy URL',
};

/** Human-facing label for any editable field id. */
function labelFor(field_id: string): string {
  if (field_id in COMPLIANCE_LABELS) {
    return COMPLIANCE_LABELS[field_id as keyof typeof COMPLIANCE_LABELS];
  }
  return (
    PROFILE_FIELDS.find((f) => f.id === field_id)?.label ?? field_id
  );
}

/** Format the proposed value as the JSON-LD literal would render on
 *  the agent card — strings double-quoted, booleans / numbers / arrays
 *  unquoted. Pure formatter over a string input; never escapes via
 *  `dangerouslySetInnerHTML`. */
function formatJsonLdValue(raw: string): string {
  if (raw.length === 0) return '""';
  if (raw.startsWith('[')) return raw;
  if (raw === 'true' || raw === 'false') return raw;
  if (!Number.isNaN(Number(raw)) && raw.trim() !== '') return raw;
  return `"${raw}"`;
}

/** Long-form fields where a `<textarea>` reads better than a single-
 *  line input. The prototype used `<input>` for everything, but the
 *  ADR's open question (§81) explicitly flags `description` as the
 *  multi-line representative case — we honor that here while keeping
 *  the rest on a single-line input.
 *
 *  Founder feedback #3 (2026-06-18): `agent_card_authoritative` is a
 *  closed enum (`'true'` | `'false'` — the server validator enforces
 *  exactly those two literals at
 *  `services/api/src/agent-card-profile/validate.ts:184-188`). It
 *  renders as a segmented Yes/No control instead of a free-text
 *  input so the merchant can't type anything that 400s. The control
 *  is handled by the `BooleanToggle` branch in the render below —
 *  we just route it here for symmetry with the other modes. */
function inputModeFor(field_id: string): 'textarea' | 'input' | 'boolean' {
  if (field_id === 'description') return 'textarea';
  if (field_id === 'agent_card_authoritative') return 'boolean';
  // ADR-0127 — `returns_policy` is free-form text up to 8 KB. A
  // textarea is the only sane shape for it; URL fields stay on a
  // single-line input.
  if (field_id === 'returns_policy') return 'textarea';
  return 'input';
}

/** Field-specific placeholder copy. The default ("Type a value…") is
 *  the catch-all; some fields have a more honest hint that helps the
 *  merchant understand what shape the server expects. Kept short
 *  enough to fit the single-line input — no expectation marketing. */
function placeholderFor(field_id: string): string {
  switch (field_id) {
    case 'description':
      return 'A sentence or two about what you sell and who you serve…';
    case 'price_range':
      return 'e.g. $$ or $20–$120';
    case 'currency':
      return 'e.g. USD';
    case 'shipping_zones':
      return 'e.g. United States, Canada';
    case 'return_window_days':
      return 'e.g. 30';
    case 'subscription_billing':
      return 'e.g. monthly, weekly';
    // ADR-0127 — compliance fields.
    case 'returns_policy':
      return 'Plain-text summary of your returns policy (free text)…';
    case 'seller_tos_url':
      return 'https://your-site.example/terms';
    case 'seller_privacy_policy_url':
      return 'https://your-site.example/privacy';
    default:
      return 'Type a value…';
  }
}

/** Modal mode (founder feedback 2026-06-18). The same editable modal
 *  serves three entry points so every field has exactly one clear
 *  action and every action ends in the typed apply route.
 *
 *   - `review` (default) — unset field WITH a suggestion. Pre-fills
 *     the input with the LLM suggestion value, renders the "WHAT WE
 *     FOUND" panel (source URL + rationale + analyzer attribution),
 *     and the apply carries the forensic `source_hint` (MH-4).
 *   - `manual` — unset field WITHOUT a suggestion. Pre-fills the
 *     input EMPTY. The "WHAT WE FOUND" panel is hidden (there is
 *     nothing to attribute). No `source_hint` on apply.
 *   - `edit` — already-set field, merchant revising the value. Pre-
 *     fills the input with the CURRENT card value. The "WHAT WE
 *     FOUND" panel is hidden. No `source_hint` on apply. */
export type SuggestionsModalMode = 'review' | 'manual' | 'edit';

/**
 * Modal-internal suggestion shape (ADR-0127). Structurally identical to
 * {@link ProfileSuggestedCardProfileField} but widens `field_id` to
 * `string` so the modal accepts both card-profile reviews (where
 * `field_id` is a `ProfileFieldId`) AND compliance synthesized edits
 * (where `field_id` is a `site_compliance` column id). The modal
 * narrows back to the valid editable union via
 * {@link isApplyableFieldId} at the apply boundary — a `field_id` that
 * isn't editable renders nothing (defensive floor; the parent's
 * routing-table-driven open handlers already filter).
 *
 * Keeping the wire-side {@link ProfileSuggestedCardProfileField}
 * structurally assignable here is the goal of widening to `string`:
 * existing callers (the ADR-0125 review path + every existing test)
 * keep working without churn.
 */
export interface EditableFieldSuggestion {
  readonly field_id: string;
  readonly value: string;
  readonly rationale: string;
  readonly source?: ProfileSuggestedCardProfileField['source'];
}

export interface SuggestionsModalProps {
  /** True to render the modal. */
  readonly open: boolean;
  /** Called when the merchant dismisses (X button, backdrop click,
   *  Cancel button). */
  readonly onClose: () => void;
  /** The single suggestion under review. In `manual` mode the parent
   *  synthesizes a placeholder ({field_id, value:'', rationale:''});
   *  in `edit` mode the parent synthesizes from the current value.
   *
   *  ADR-0127 widens the accepted `field_id` to cover the three
   *  compliance fields (`returns_policy`, `seller_tos_url`,
   *  `seller_privacy_policy_url`). A `ProfileSuggestedCardProfileField`
   *  is structurally assignable here — only the union of accepted IDs
   *  is wider. */
  readonly suggestion: EditableFieldSuggestion;
  /** Modal mode — controls the layout + apply payload shape. See
   *  {@link SuggestionsModalMode}. Defaults to `'review'`. */
  readonly mode?: SuggestionsModalMode;
  /** Server-known URL of the page the suggestion was attributed to
   *  (homepage URL is the safe default). Rendered as INERT TEXT in
   *  `review` mode only — never a link, never interpolated into
   *  `href` / `src` / `style` / `onClick`. See MH-2 in the file
   *  header. */
  readonly source_page_url: string;
  /** The existing card value for this field, or null when no row /
   *  no value is set. Rendered as inert text in the CURRENT VALUE
   *  tile; "null" is shown when this is null. */
  readonly current_value?: string | null;
  /** Carries through to the apply call's source_hint (ADR-0114 §5.1).
   *  The parent computes the content hash and posts. Required in
   *  `review` mode; the parent ignores it in `manual` / `edit`. */
  readonly profileExtractionId: string;
  /** The eTag for the merchant's current `agent_card_profile` row.
   *  `*` if no row yet. Read by the parent on apply. */
  readonly etag: string;
  /** True while the apply POST is in flight. */
  readonly pending: boolean;
  /** Single-field apply. The modal passes the merchant's edited value
   *  (which may equal the suggestion's original value). The parent
   *  calls `computeSuggestionContentHash` over the DISPLAYED
   *  suggestion (NOT the edited value, per MH-4) and posts to
   *  `POST /v1/sites/:site_id/agent-card-profile`. */
  /**
   *  ADR-0127 — the `fields` map is widened from `ApplyFieldName` to
   *  `AgentCardEditableFieldId` so a single onApply contract serves
   *  both card-profile saves AND compliance saves. The parent dispatches
   *  to either `applyAgentCardProfile` (eTag path) or
   *  `updateSiteCompliance` (no eTag) via the FIELD_ROUTING table. The
   *  `source_hint` is forwarded only for card-profile review-mode
   *  applies; the parent ignores it for compliance applies (no
   *  forensic-hash contract on the compliance route).
   */
  readonly onApply: (input: {
    readonly fields: Partial<Record<AgentCardEditableFieldId, string>>;
    readonly source_hint: ApplySourceHint;
  }) => void | Promise<void>;
  /** Called when the merchant clicks "Refresh" after a 412 stale-eTag
   *  banner. The parent re-fetches `getLatest()` + the latest profile
   *  eTag, then re-renders the modal with fresh props. */
  readonly onRefreshRequested?: () => void;
  /** Live-preview hook (founder feedback #3). Fires on every keystroke
   *  in the input so the parent can overlay the pending edited value
   *  on top of `fields` and surface a mini before/after in
   *  HumanView / AgentView even while the modal covers the right panel.
   *  Optional — when absent the modal is purely local-state.
   *
   *  The callback receives the unsanitized edit; the parent renders it
   *  through the same React text-node escape boundary as `fields` so
   *  no new untrusted-output surface is opened. */
  readonly onValueChange?: (field_id: string, value: string) => void;
  /** The failure surface from the most-recent apply, if any. */
  readonly applyFailure?: {
    readonly kind:
      | 'concurrency_mismatch'
      | 'forbidden'
      | 'validation'
      | 'not_found'
      | 'precondition_required'
      | 'unauthorized'
      | 'unknown';
    readonly server_reason?: string;
    readonly field?: string;
  };
}

export function SuggestionsModal({
  open,
  onClose,
  suggestion,
  mode = 'review',
  source_page_url,
  current_value,
  profileExtractionId,
  etag,
  pending,
  onApply,
  onRefreshRequested,
  onValueChange,
  applyFailure,
}: SuggestionsModalProps): JSX.Element | null {
  const C = useC();

  // Capture the ORIGINAL displayed value at modal open. The "you
  // edited this" banner compares the live input against this baseline
  // (NOT the current `suggestion.value`, because a future prop update
  // shouldn't flip the banner on its own). Per ADR-0125 §Consequences:
  // banner state = `edited.trim() !== initial.trim()`, captured once.
  //
  // We also gate the rest of the local state on a stable per-open
  // identity: a parent that swaps the `suggestion` prop while the
  // modal stays open (rare but possible — e.g. a re-fetch) reseats
  // the input + the checkbox + the initial baseline.
  const stableSuggestionKey = `${mode}::${suggestion.field_id}::${suggestion.value}`;

  const initialValueRef = useStableInitial(stableSuggestionKey, suggestion.value, open);

  // Founder feedback #3 — boolean fields canonicalize to 'true' /
  // 'false' string literals (the server's CHECK constraint). An
  // empty initial value (manual mode / fresh INSERT) defaults to
  // 'false' so the apply payload always carries one of the two
  // accepted literals, never an empty string. Edit mode preserves
  // whatever the row currently carries.
  const isBooleanField = suggestion.field_id === 'agent_card_authoritative';
  const seedValue = isBooleanField
    ? suggestion.value === 'true'
      ? 'true'
      : 'false'
    : suggestion.value;
  const [editedValue, setEditedValue] = useState<string>(seedValue);
  const [acknowledged, setAcknowledged] = useState<boolean>(false);
  // Founder feedback #8 (2026-06-18): the input was visually inert
  // (`bg-transparent` + no border) so merchants didn't read it as
  // editable. We add a visible border + subtle surface background +
  // a lime focus ring + placeholder so the field reads as an input.
  // Focus tracking lives in React state because inline styles can't
  // express `:focus` (we render bespoke styling rather than pull in
  // a CSS module just for one component). The state is purely
  // visual — no policy hook, no side effects.
  const [inputFocused, setInputFocused] = useState<boolean>(false);

  useEffect(() => {
    if (!open) return;
    setEditedValue(seedValue);
    setAcknowledged(false);
  }, [open, stableSuggestionKey, seedValue]);

  // Live-preview hook — fire once on open (so the parent gets the
  // baseline value to overlay) and on every keystroke. Cleared on
  // close in the parent's `handleCloseModal`.
  useEffect(() => {
    if (!open) return;
    if (onValueChange === undefined) return;
    onValueChange(suggestion.field_id, editedValue);
  }, [open, onValueChange, suggestion.field_id, editedValue]);

  // Defensive: an unapplyable `field_id` (e.g. `name`) reaching this
  // modal would mean the parent's filter failed. Render nothing so we
  // never POST a non-writable field. The parent's filter at
  // Visibility.tsx already excludes these, but the floor is here too.
  const isApplyable = isApplyableFieldId(suggestion.field_id as string);

  const label = labelFor(suggestion.field_id);
  const isStructured = suggestion.source === 'structured';
  const isStaleEtag = applyFailure?.kind === 'concurrency_mismatch';
  const validationError =
    applyFailure?.kind === 'validation' ? applyFailure : null;

  // Founder feedback #1 + #4 — the same modal serves three entry
  // points. The "WHAT WE FOUND" panel only makes sense in `review`
  // mode (where there's a real suggestion to attribute); manual /
  // edit modes hide it and let the right column take the full width.
  const showFoundPanel = mode === 'review';
  const titleCopy =
    mode === 'manual'
      ? `Set manually: ${label}`
      : mode === 'edit'
        ? `Edit: ${label}`
        : `Review proposed change: ${label}`;
  const subtitleCopy =
    mode === 'manual'
      ? 'Type the value you want agents to read for this field. Sill never edits your agent card without your explicit approval.'
      : mode === 'edit'
        ? 'Revise this field. Your update will replace what agents currently read. Sill never edits your agent card without your explicit approval.'
        : "Sill never edits your agent card without your explicit approval. Review the source we read and the value we propose, then approve or adjust.";
  const applyButtonCopy =
    mode === 'manual'
      ? 'Save & apply'
      : mode === 'edit'
        ? 'Save changes'
        : 'Approve & apply';
  const ackCopy =
    mode === 'manual'
      ? "I've reviewed the value I'm about to save."
      : mode === 'edit'
        ? "I've reviewed the revised value."
        : "I've reviewed the source and the proposed value.";

  const valueEdited = useMemo(
    () => editedValue.trim() !== initialValueRef.current.trim(),
    [editedValue, initialValueRef],
  );

  const inputMode = inputModeFor(suggestion.field_id);

  // Document hash discipline at the call site (MH-4): the parent
  // computes the hash over the DISPLAYED suggestion array, not over
  // `editedValue`. This component only forwards the edited value
  // through `onApply`.
  void etag; // forwarded by parent; modal doesn't thread directly.
  void profileExtractionId; // forwarded by parent's hash + source_hint.

  // ADR-0127 — client-side validation gate. The server validators on
  // both routes (agent-card-profile + compliance) remain the
  // authoritative floor; this check exists so a `seller_tos_url`
  // value missing `https://` (and similar shape errors) surfaces a
  // friendly inline error WITHOUT a round trip. The error is rendered
  // by the existing validation banner using the same `server_reason`
  // discriminator the route would emit on a 400.
  const clientValidation = useMemo(() => {
    if (!isApplyable) return { ok: true } as const;
    return validateFieldValue(
      suggestion.field_id as AgentCardEditableFieldId,
      editedValue,
    );
  }, [editedValue, isApplyable, suggestion.field_id]);

  const handleApply = async (): Promise<void> => {
    if (pending) return;
    if (!acknowledged) return;
    if (!isApplyable) return;
    if (!clientValidation.ok) return;
    const fieldName = suggestion.field_id as AgentCardEditableFieldId;
    // MH-3: write authority is THIS click. We forward the merchant's
    // edited value (may equal the original). The parent computes the
    // content hash over the DISPLAYED suggestion (NOT the edited
    // value) and threads it into `source_hint` per MH-4 for card-
    // profile review applies. ADR-0127: for compliance applies the
    // parent ignores the source_hint and posts a partial-merge PUT
    // with no eTag.
    await onApply({
      fields: { [fieldName]: editedValue } as Partial<
        Record<AgentCardEditableFieldId, string>
      >,
      // The parent owns the hash + the source_hint construction (so
      // it can use `computeSuggestionContentHash` from the api bag).
      // We pass a stub source_hint here that the parent overrides if
      // it likes, but our contract per ADR-0125 §Data shapes is for
      // the parent to inject the hash. To keep the test surface tight
      // we forward an empty hash; the parent's `handleApply` ignores
      // this and computes its own. The lint-friendly empty string is
      // a recognized "not computed" marker (the server rejects empty
      // hashes via the validate route's shape check — defensive).
      source_hint: {
        profile_extraction_id: profileExtractionId,
        suggestion_content_hash: '',
      },
    });
  };

  if (!open) return null;
  if (!isApplyable) return null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title={titleCopy}
      subtitle={subtitleCopy}
      closeOnBackdrop={false}
    >
      {isStaleEtag && (
        <div
          className="flex items-start gap-3 p-4 mx-5 mt-5 lg:mx-6"
          style={{
            background: C.amberDim,
            border: `1px solid ${C.amberFg}`,
            borderRadius: 2,
          }}
          role="status"
        >
          <AlertCircle
            size={14}
            color={C.amberFg}
            className="mt-0.5 flex-shrink-0"
          />
          <div className="flex-1 min-w-0">
            <div
              style={{
                fontFamily: sansFont,
                fontSize: 13,
                color: C.text,
                fontWeight: 500,
                marginBottom: 4,
              }}
            >
              Another admin updated this site.
            </div>
            <div
              style={{
                fontFamily: sansFont,
                fontSize: 12,
                color: C.muted,
                lineHeight: 1.5,
              }}
            >
              Refresh suggestions to see the latest value, then apply
              again.
            </div>
            {onRefreshRequested !== undefined && (
              <button
                type="button"
                onClick={onRefreshRequested}
                className="mt-3 px-3 py-1.5"
                style={{
                  background: C.surface,
                  color: C.text,
                  border: `1px solid ${C.border}`,
                  borderRadius: 2,
                  fontFamily: sansFont,
                  fontSize: 12,
                  fontWeight: 500,
                }}
              >
                Refresh suggestions
              </button>
            )}
          </div>
        </div>
      )}

      <div
        className="flex flex-col lg:flex-row"
        style={{ borderBottom: `1px solid ${C.border}` }}
      >
        {showFoundPanel ? (
        /* LEFT — WHAT WE FOUND. Provenance is server-known: the page
            URL we scraped + the LLM's rationale. No verbatim quote,
            no line number — those re-open the fence-marker vector
            (ADR-0125 §Alternatives considered). The URL is INERT
            TEXT (MH-2). Hidden in `manual` / `edit` modes — there is
            no suggestion to attribute. */
        <div
          className="p-5 lg:p-6 flex-1 min-w-0"
          style={{ borderRight: `1px solid ${C.borderSoft}` }}
        >
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <Eye size={11} color={C.skyFg} />
            <span
              style={{
                fontFamily: monoFont,
                fontSize: 9,
                color: C.muted,
                letterSpacing: 1.5,
              }}
            >
              WHAT WE FOUND
            </span>
            {/* ADR-0118 Slice B — preserve the structured-source badge.
                Replaces the "SUGGESTED BY SILL'S SITE ANALYZER" cue
                on structured rows so the merchant doesn't conflate
                their own catalog/compliance data with LLM output.
                Scrape rows render the analyzer attribution below. */}
            {isStructured ? (
              <span
                data-testid={`source-badge-structured-${suggestion.field_id}`}
                style={{
                  fontFamily: monoFont,
                  fontSize: 9,
                  letterSpacing: 1.5,
                  color: C.muted,
                  background: C.surface2,
                  border: `1px solid ${C.borderSoft}`,
                  borderRadius: 2,
                  padding: '1px 6px',
                  fontWeight: 500,
                  marginLeft: 4,
                }}
              >
                FROM YOUR{' '}
                {suggestion.field_id === 'return_window_days'
                  ? 'COMPLIANCE'
                  : 'CATALOG'}
              </span>
            ) : null}
            <span
              style={{
                fontFamily: monoFont,
                fontSize: 9,
                color: C.dim,
                marginLeft: 'auto',
              }}
            >
              SOURCE
            </span>
          </div>

          {/* Source URL line. INERT TEXT — never a link. The URL is
              attacker-influenceable (the merchant's site is the
              untrusted surface); a live `<a href>` would re-open the
              attack vector ADR-0040 closes. */}
          <div
            className="flex items-center gap-1.5 mb-3 flex-wrap"
            style={{
              fontFamily: monoFont,
              fontSize: 11,
              color: C.text,
            }}
          >
            <span style={{ color: C.skyFg }}>GET</span>
            <span
              data-testid="source-page-url"
              dir="auto"
              style={{
                overflowWrap: 'anywhere',
                wordBreak: 'break-word',
                color: C.text,
              }}
            >
              {source_page_url}
            </span>
          </div>

          {/* Rationale — only for scrape-sourced (LLM) rows. UNTRUSTED;
              rendered as a React text node (auto-escaped). Structured
              rows skip this because the merchant's own data needs no
              explanation. */}
          {isStructured ? (
            <div
              className="p-3"
              style={{
                background: C.surface,
                border: `1px solid ${C.border}`,
                borderRadius: 2,
              }}
            >
              <div
                style={{
                  fontFamily: monoFont,
                  fontSize: 9,
                  color: C.muted,
                  letterSpacing: 1,
                  marginBottom: 6,
                }}
              >
                FROM YOUR OWN DATA
              </div>
              <div
                style={{
                  fontFamily: sansFont,
                  fontSize: 13,
                  color: C.muted,
                  lineHeight: 1.5,
                }}
              >
                This came from your catalog or compliance settings, not
                from a scrape of your site.
              </div>
            </div>
          ) : (
            <div
              className="p-3"
              style={{
                background: C.surface,
                border: `1px solid ${C.border}`,
                borderRadius: 2,
              }}
            >
              <div
                style={{
                  fontFamily: monoFont,
                  fontSize: 9,
                  color: C.muted,
                  letterSpacing: 1,
                  marginBottom: 6,
                }}
              >
                WHY WE PROPOSED THIS
              </div>
              <div
                dir="auto"
                data-testid={`suggested-rationale-${suggestion.field_id}`}
                style={{
                  fontFamily: sansFont,
                  fontSize: 13,
                  color: C.text,
                  lineHeight: 1.6,
                  overflowWrap: 'anywhere',
                  wordBreak: 'break-word',
                }}
              >
                {suggestion.rationale}
              </div>
            </div>
          )}

          {!isStructured && (
            <div
              className="mt-3 px-3 py-2 flex items-start gap-2"
              data-testid="analyzer-attribution-badge"
              style={{
                background: C.surface2,
                border: `1px solid ${C.border}`,
                borderRadius: 2,
              }}
            >
              <Info
                size={11}
                color={C.muted}
                className="flex-shrink-0 mt-0.5"
              />
              <div
                style={{
                  fontFamily: monoFont,
                  fontSize: 9,
                  color: C.muted,
                  letterSpacing: 1.5,
                  fontWeight: 500,
                }}
              >
                SUGGESTED BY SILL'S SITE ANALYZER
              </div>
            </div>
          )}
        </div>
        ) : null}

        {/* RIGHT — PROPOSED UPDATE. Current value tile + editable
            proposed value tile + live JSON-LD diff. The kicker copy
            adapts per mode so the merchant reads the surface as the
            same idea ("here is what will be saved") in all three
            entry points. */}
        <div className="p-5 lg:p-6 flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <Sparkles size={11} color={C.limeFg} />
            <span
              style={{
                fontFamily: monoFont,
                fontSize: 9,
                color: C.muted,
                letterSpacing: 1.5,
              }}
            >
              {mode === 'manual'
                ? 'NEW VALUE'
                : mode === 'edit'
                  ? 'REVISED VALUE'
                  : 'PROPOSED UPDATE'}
            </span>
          </div>
          <div
            style={{
              fontFamily: monoFont,
              fontSize: 11,
              color: C.muted,
              marginBottom: 8,
            }}
          >
            agent-card.json →{' '}
            <span style={{ color: C.text }}>{suggestion.field_id}</span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
            <div
              className="p-3"
              style={{
                background: C.surface2,
                border: `1px solid ${C.border}`,
                borderRadius: 2,
              }}
            >
              <div
                style={{
                  fontFamily: monoFont,
                  fontSize: 9,
                  color: C.muted,
                  letterSpacing: 1.5,
                  marginBottom: 6,
                }}
              >
                CURRENT VALUE
              </div>
              <div className="flex items-center gap-2">
                {current_value === null || current_value === undefined ? (
                  <>
                    <X size={11} color={C.coralFg} />
                    <span
                      style={{
                        fontFamily: monoFont,
                        fontSize: 12,
                        color: C.muted,
                      }}
                    >
                      null
                    </span>
                  </>
                ) : (
                  <span
                    dir="auto"
                    data-testid="current-value"
                    style={{
                      fontFamily: monoFont,
                      fontSize: 12,
                      color: C.text,
                      overflowWrap: 'anywhere',
                      wordBreak: 'break-word',
                    }}
                  >
                    {current_value}
                  </span>
                )}
              </div>
            </div>
            <div
              className="p-3"
              style={{
                background: C.limeDim,
                border: `1px solid ${C.limeFg}`,
                borderRadius: 2,
              }}
            >
              <div
                style={{
                  fontFamily: monoFont,
                  fontSize: 9,
                  color: C.limeFg,
                  letterSpacing: 1.5,
                  marginBottom: 6,
                }}
              >
                {mode === 'manual'
                  ? 'NEW VALUE'
                  : mode === 'edit'
                    ? 'REVISED VALUE'
                    : 'PROPOSED VALUE'}
              </div>
              {/* Editable input. MH-3: onChange writes LOCAL React
                  state only. No client-side validation; the server
                  validator at /v1/sites/:id/agent-card-profile re-
                  sanitizes and is the actual gate. dir="auto" so RTL
                  / CJK render in the correct direction.
                  Founder feedback #8 (2026-06-18): the input now has
                  a visible border, a subtle surface background, a
                  lime focus ring, and a placeholder when empty. The
                  outer tile keeps the lime accent kicker; the input
                  itself is its own framed surface so the merchant
                  reads it as obviously editable.
                  Founder feedback #3 (2026-06-18):
                  agent_card_authoritative renders as a Yes / No
                  segmented control instead of free text — the
                  server's CHECK constraint accepts only the literal
                  strings 'true' / 'false', so a text field is a
                  guaranteed 400 the merchant has no way to debug. */}
              {inputMode === 'boolean' ? (
                <BooleanToggle
                  field_id={suggestion.field_id}
                  value={editedValue}
                  onChange={setEditedValue}
                  label={label}
                />
              ) : inputMode === 'textarea' ? (
                <textarea
                  data-testid={`suggested-value-${suggestion.field_id}`}
                  value={editedValue}
                  onChange={(e) => setEditedValue(e.target.value)}
                  onFocus={() => setInputFocused(true)}
                  onBlur={() => setInputFocused(false)}
                  dir="auto"
                  rows={6}
                  placeholder={placeholderFor(suggestion.field_id)}
                  aria-label={`Proposed value for ${label}`}
                  className="w-full outline-none resize-y px-2.5 py-2"
                  style={{
                    fontFamily: monoFont,
                    fontSize: 12,
                    color: C.text,
                    lineHeight: 1.6,
                    minHeight: 120,
                    overflowWrap: 'anywhere',
                    wordBreak: 'break-word',
                    background: C.surface,
                    border: `1px solid ${inputFocused ? C.lime : C.border}`,
                    borderRadius: 2,
                    boxShadow: inputFocused
                      ? '0 0 0 2px rgba(217,255,63,0.18)'
                      : 'none',
                    transition: 'border-color 120ms ease, box-shadow 120ms ease',
                  }}
                />
              ) : (
                <input
                  data-testid={`suggested-value-${suggestion.field_id}`}
                  type="text"
                  value={editedValue}
                  onChange={(e) => setEditedValue(e.target.value)}
                  onFocus={() => setInputFocused(true)}
                  onBlur={() => setInputFocused(false)}
                  dir="auto"
                  placeholder={placeholderFor(suggestion.field_id)}
                  aria-label={`Proposed value for ${label}`}
                  className="w-full outline-none px-2.5 py-2"
                  style={{
                    fontFamily: monoFont,
                    fontSize: 12,
                    color: C.text,
                    background: C.surface,
                    border: `1px solid ${inputFocused ? C.lime : C.border}`,
                    borderRadius: 2,
                    boxShadow: inputFocused
                      ? '0 0 0 2px rgba(217,255,63,0.18)'
                      : 'none',
                    transition: 'border-color 120ms ease, box-shadow 120ms ease',
                  }}
                />
              )}
              {inputMode === 'boolean' && (
                <div
                  data-testid={`boolean-toggle-help-${suggestion.field_id}`}
                  style={{
                    fontFamily: sansFont,
                    fontSize: 11,
                    color: C.muted,
                    lineHeight: 1.5,
                    marginTop: 8,
                  }}
                >
                  Mark this card as the authoritative source agents
                  should trust for these answers.
                </div>
              )}
            </div>
          </div>

          {/* JSON-LD diff. Re-renders live as `editedValue` changes. */}
          <div
            className="p-3"
            data-testid="json-ld-diff"
            style={{
              background: C.surface,
              border: `1px solid ${C.border}`,
              borderRadius: 2,
            }}
          >
            <div
              style={{
                fontFamily: monoFont,
                fontSize: 9,
                color: C.muted,
                letterSpacing: 1.5,
                marginBottom: 6,
              }}
            >
              JSON-LD DIFF
            </div>
            <pre
              style={{
                fontFamily: monoFont,
                fontSize: 11,
                lineHeight: 1.7,
                margin: 0,
                whiteSpace: 'pre-wrap',
                overflowWrap: 'anywhere',
                wordBreak: 'break-word',
              }}
            >
              <span style={{ color: C.coralFg }}>- </span>
              <span
                style={{
                  color: C.muted,
                  textDecoration: 'line-through',
                }}
              >
                "{suggestion.field_id}":{' '}
                {current_value === null || current_value === undefined
                  ? 'null'
                  : formatJsonLdValue(current_value)}
              </span>
              {'\n'}
              <span style={{ color: C.limeFg }}>+ </span>
              <span style={{ color: C.text }}>
                "{suggestion.field_id}": {formatJsonLdValue(editedValue)}
              </span>
            </pre>
          </div>

          {valueEdited && mode === 'review' && (
            <div
              className="mt-3 flex items-center gap-2 px-2.5 py-1.5"
              data-testid="value-edited-banner"
              style={{
                background: C.amberDim,
                border: `1px solid ${C.amberFg}`,
                borderRadius: 2,
              }}
            >
              <AlertTriangle
                size={10}
                color={C.amberFg}
                className="flex-shrink-0"
              />
              <span
                style={{
                  fontFamily: sansFont,
                  fontSize: 11,
                  color: C.text,
                }}
              >
                You edited the proposed value. Your version will be
                saved instead of ours.
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Approval gate — required checkbox. */}
      <div className="p-5 lg:p-6">
        <button
          type="button"
          onClick={() => setAcknowledged((prev) => !prev)}
          aria-pressed={acknowledged}
          data-testid="ack-checkbox"
          className="flex items-start gap-3 text-left w-full"
        >
          {/* Founder feedback #7 (2026-06-18) — checked state uses
              C.lime (#D9FF3F, identical in both themes) with C.ink
              for the check glyph, MATCHING the Apply button's
              C.lime + C.ink pairing. Previously the checked state
              used C.limeFg, which is #3D5500 (dark olive) in light
              mode — off-brand and visually confused with a
              disabled state. The unchecked state stays
              transparent + C.border because the unchecked tile
              should read as a surface affordance, not a colored
              chip. */}
          <div
            className="flex items-center justify-center flex-shrink-0 mt-0.5"
            style={{
              width: 18,
              height: 18,
              background: acknowledged ? C.lime : 'transparent',
              border: `1.5px solid ${acknowledged ? C.lime : C.border}`,
              borderRadius: 2,
            }}
          >
            {acknowledged && <Check size={12} color={C.ink} strokeWidth={3} />}
          </div>
          <div className="flex-1">
            <div
              style={{
                fontFamily: sansFont,
                fontSize: 13,
                color: C.text,
                fontWeight: 500,
                lineHeight: 1.4,
              }}
            >
              {ackCopy}
            </div>
            <div
              style={{
                fontFamily: sansFont,
                fontSize: 12,
                color: C.muted,
                lineHeight: 1.5,
                marginTop: 4,
              }}
            >
              Required before applying. This becomes the agent-
              readable answer to questions about{' '}
              <em>{label.toLowerCase()}</em>. Wrong here means wrong
              everywhere agents quote your site.
            </div>
          </div>
        </button>
      </div>

      {validationError !== null && (
        <div
          className="flex items-start gap-3 p-3 mx-5 lg:mx-6 mb-3"
          style={{
            background: C.coralDim,
            border: `1px solid ${C.coralFg}`,
            borderRadius: 2,
          }}
          role="alert"
        >
          <X
            size={14}
            color={C.coralFg}
            className="mt-0.5 flex-shrink-0"
          />
          <div className="flex-1 min-w-0">
            <div
              style={{
                fontFamily: sansFont,
                fontSize: 12,
                color: C.text,
                fontWeight: 500,
                marginBottom: 2,
              }}
            >
              Couldn't apply this change.
            </div>
            <div
              style={{
                fontFamily: monoFont,
                fontSize: 11,
                color: C.muted,
                lineHeight: 1.5,
              }}
            >
              {validationError.server_reason ?? 'validation_failed'}
              {validationError.field !== undefined
                ? ` · ${validationError.field}`
                : ''}
            </div>
          </div>
        </div>
      )}

      {/* ADR-0127 — client-side validation banner. Shown when the
          merchant's edited value fails the per-field validation
          (e.g. a `seller_tos_url` missing `https://`) BEFORE the
          apply round-trip. The server-side 400 banner above remains
          the floor; this one shortens the feedback loop without
          replacing the floor. Suppressed for empty values — the
          server treats empty as "leave unchanged" on the card route
          and "clear" on the compliance route; the merchant intent
          is honored either way. */}
      {!clientValidation.ok
        && editedValue.length > 0
        && validationError === null && (
          <div
            className="flex items-start gap-3 p-3 mx-5 lg:mx-6 mb-3"
            data-testid="client-validation-error"
            style={{
              background: C.coralDim,
              border: `1px solid ${C.coralFg}`,
              borderRadius: 2,
            }}
            role="alert"
          >
            <X
              size={14}
              color={C.coralFg}
              className="mt-0.5 flex-shrink-0"
            />
            <div className="flex-1 min-w-0">
              <div
                style={{
                  fontFamily: sansFont,
                  fontSize: 12,
                  color: C.text,
                  fontWeight: 500,
                  marginBottom: 2,
                }}
              >
                This value doesn't match the expected shape.
              </div>
              <div
                style={{
                  fontFamily: monoFont,
                  fontSize: 11,
                  color: C.muted,
                  lineHeight: 1.5,
                }}
              >
                {clientValidation.reason}
              </div>
            </div>
          </div>
        )}

      <div
        className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center gap-2 px-5 lg:px-6 py-4 flex-shrink-0"
        style={{
          borderTop: `1px solid ${C.border}`,
          background: C.surface,
        }}
      >
        <button
          type="button"
          onClick={onClose}
          disabled={pending}
          className="px-4 py-2.5"
          style={{
            background: 'transparent',
            border: `1px solid ${C.border}`,
            color: C.muted,
            fontFamily: sansFont,
            fontSize: 13,
            borderRadius: 2,
            cursor: pending ? 'not-allowed' : 'pointer',
            opacity: pending ? 0.5 : 1,
          }}
        >
          Cancel
        </button>
        {(() => {
          // ADR-0127 — the apply button is enabled only when ack +
          // client validation pass. Empty values pass client
          // validation (the server treats them as clear/no-op per
          // route); a non-empty value that fails (e.g. a
          // `seller_tos_url` missing `https://`) blocks the apply
          // until the merchant fixes it.
          const clientOk = clientValidation.ok || editedValue.length === 0;
          const canApply = acknowledged && !pending && clientOk;
          return (
            <button
              type="button"
              onClick={() => void handleApply()}
              disabled={!canApply}
              data-testid="apply-button"
              className="flex-1 sm:flex-none sm:ml-auto px-6 py-2.5 flex items-center justify-center gap-2"
              style={{
                background: canApply ? C.lime : C.surface2,
                color: canApply ? C.ink : C.muted,
                border: `1px solid ${canApply ? C.lime : C.border}`,
                fontFamily: sansFont,
                fontWeight: 600,
                fontSize: 13,
                borderRadius: 2,
                opacity: canApply ? 1 : 0.6,
                cursor: canApply ? 'pointer' : 'not-allowed',
              }}
            >
              <Check size={11} />
              {pending ? 'Applying…' : applyButtonCopy}
            </button>
          );
        })()}
      </div>
    </Modal>
  );
}

/**
 * Two-option segmented control for closed-enum boolean fields
 * (currently only `agent_card_authoritative`, ADR-0125 + founder
 * feedback #3, 2026-06-18). The server's CHECK constraint at
 * `services/api/src/agent-card-profile/validate.ts:184-188` accepts
 * exactly the string literals `'true'` and `'false'`; any other
 * value produces an `invalid_agent_card_authoritative_value` 400 the
 * merchant has no way to recover from in a text input. Rendering as
 * a toggle removes the failure mode entirely — the only values the
 * UI can produce are the two the server accepts.
 *
 * The control is its own component so future closed-enum fields
 * (subscription cadence, currency dropdown) can reuse the segmented
 * pattern without re-writing the visual idiom. Yes / No copy is the
 * merchant-facing translation of the server's true/false literal.
 */
function BooleanToggle({
  field_id,
  value,
  onChange,
  label,
}: {
  field_id: string;
  value: string;
  onChange: (next: string) => void;
  label: string;
}): JSX.Element {
  const C = useC();
  // Treat anything non-true as "No" — the input may arrive as an
  // empty string (manual mode, fresh INSERT) or as a stale string
  // from a prior session. The control collapses every non-'true'
  // state into the No segment so the merchant sees a definite
  // default rather than an "is this thing on?" tri-state.
  const isYes = value === 'true';
  const buttonStyle = (active: boolean): CSSProperties => ({
    flex: 1,
    padding: '8px 12px',
    fontFamily: sansFont,
    fontSize: 12,
    fontWeight: active ? 600 : 500,
    background: active ? C.lime : C.surface,
    color: active ? C.ink : C.text,
    border: `1px solid ${active ? C.lime : C.border}`,
    borderRadius: 2,
    cursor: 'pointer',
    transition: 'background-color 120ms ease, color 120ms ease',
  });
  return (
    <div
      role="radiogroup"
      aria-label={`Proposed value for ${label}`}
      data-testid={`suggested-value-${field_id}`}
      // Hidden form-style `data-value` so tests can read the live
      // string the toggle will forward to the apply payload without
      // having to introspect the lit segment's class state.
      data-value={isYes ? 'true' : 'false'}
      className="flex items-stretch gap-2"
    >
      <button
        type="button"
        role="radio"
        aria-checked={isYes}
        data-testid={`boolean-toggle-yes-${field_id}`}
        onClick={() => onChange('true')}
        style={buttonStyle(isYes)}
      >
        Yes
      </button>
      <button
        type="button"
        role="radio"
        aria-checked={!isYes}
        data-testid={`boolean-toggle-no-${field_id}`}
        onClick={() => onChange('false')}
        style={buttonStyle(!isYes)}
      >
        No
      </button>
    </div>
  );
}

/**
 * Lightweight ref-with-init: captures the initial value on the FIRST
 * render after `open` flips to true (or when the suggestion identity
 * changes). The "you edited this" banner compares the live input
 * against THIS baseline so a merchant who types the original value
 * back in sees the banner clear (ADR-0125 §Consequences). We track
 * the suggestion identity via `key` so a re-fetch that swaps the
 * suggestion prop re-baselines the comparison.
 */
function useStableInitial(
  key: string,
  value: string,
  open: boolean,
): { current: string } {
  const [state, setState] = useState<{ key: string; value: string }>({
    key,
    value,
  });
  useEffect(() => {
    if (!open) return;
    setState((prev) => {
      if (prev.key === key) return prev;
      return { key, value };
    });
  }, [key, value, open]);
  // Re-baseline on the first open after a fresh suggestion identity.
  // Returning a `{ current }` shape so the caller's API matches what
  // it would get from `useRef`.
  return { current: state.key === key ? state.value : value };
}
